import type { ReactNode, RefObject } from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useInfiniteScrollVisits } from 'hooks/use-infinite-scroll-visits.hook';
import { VisitUiType } from 'store/visits/types';
import { Loader } from 'components/shared/loader';
import { Button } from '@hqo/ui-atoms';

export interface InfiniteScrollState {
  hasMore: boolean;
  isLoadingMore: boolean;
  observerTarget: RefObject<HTMLElement | null>;
  error: Error | null;
  retry: () => void;
  nextOffset: number;
}

interface InfiniteScrollContainerBaseProps {
  buildingId?: string;
  after?: string;
  before?: string;
  children: ReactNode;
  variant?: 'table' | 'card';
}

interface InfiniteScrollContainerProps extends InfiniteScrollContainerBaseProps {
  /** When provided, use this instead of calling the hook (avoids duplicate hook usage in parent) */
  infiniteScrollState?: InfiniteScrollState;
}

const INITIAL_BATCH_SIZE = 50;

const InfiniteScrollContainerView = ({
  children,
  variant,
  hasMore,
  isLoadingMore,
  observerTarget,
  error,
  retry,
  nextOffset,
  noMoreMessage,
  retryButtonText,
  wrapInTableRow,
}: {
  children: ReactNode;
  variant: 'table' | 'card';
  hasMore: boolean;
  isLoadingMore: boolean;
  observerTarget: RefObject<HTMLElement | null>;
  error: Error | null;
  retry: () => void;
  nextOffset: number;
  noMoreMessage: string;
  retryButtonText: string;
  wrapInTableRow: (content: ReactNode) => ReactNode;
}) => (
  <>
    {children}
    {isLoadingMore &&
      wrapInTableRow(
        <div className="flex items-center justify-center">
          <Loader />
        </div>,
      )}
    {error &&
      wrapInTableRow(
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-red-600">Failed to load more visits</p>
          <Button onClick={retry}>{retryButtonText}</Button>
        </div>,
      )}
    {!hasMore &&
      !isLoadingMore &&
      nextOffset > INITIAL_BATCH_SIZE &&
      wrapInTableRow(<p className="text-sm text-gray-500">{noMoreMessage}</p>)}
    {hasMore &&
      !error &&
      (variant === 'table' ? (
        <tr ref={observerTarget as RefObject<HTMLTableRowElement>}>
          <td colSpan={7} className="h-4" />
        </tr>
      ) : (
        <div ref={observerTarget as RefObject<HTMLDivElement>} className="h-4" />
      ))}
  </>
);

/** Uses the hook internally (for mobile or when parent does not pass state). */
const InfiniteScrollContainerWithHook = ({
  buildingId,
  after,
  before,
  children,
  variant = 'card',
}: InfiniteScrollContainerBaseProps) => {
  const { formatMessage } = useIntl();
  const { hasMore, isLoadingMore, observerTarget, error, retry, nextOffset } = useInfiniteScrollVisits({
    buildingId,
    type: VisitUiType.ALL,
    batchSize: INITIAL_BATCH_SIZE,
    after,
    before,
  });
  const noMoreMessage = formatMessage({
    id: 'visitorRegistration.labels.noMoreVisits',
    defaultMessage: 'No more visits',
  });
  const retryButtonText = formatMessage({
    id: 'visitorRegistration.buttons.retry',
    defaultMessage: 'Retry',
  });
  const wrapInTableRow = useCallback(
    (content: ReactNode) =>
      variant === 'table' ? (
        <tr>
          <td colSpan={7} className="px-4 py-6 text-center">
            {content}
          </td>
        </tr>
      ) : (
        <div className="text-center w-full py-6">{content}</div>
      ),
    [variant],
  );
  return (
    <InfiniteScrollContainerView
      variant={variant}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      observerTarget={observerTarget}
      error={error}
      retry={retry}
      nextOffset={nextOffset}
      noMoreMessage={noMoreMessage}
      retryButtonText={retryButtonText}
      wrapInTableRow={wrapInTableRow}
    >
      {children}
    </InfiniteScrollContainerView>
  );
};

export const InfiniteScrollContainer = (props: InfiniteScrollContainerProps) => {
  const { infiniteScrollState, buildingId, after, before, children, variant = 'card' } = props;
  const { formatMessage } = useIntl();
  const wrapInTableRow = useCallback(
    (content: ReactNode) =>
      variant === 'table' ? (
        <tr>
          <td colSpan={7} className="px-4 py-6 text-center">
            {content}
          </td>
        </tr>
      ) : (
        <div className="text-center w-full py-6">{content}</div>
      ),
    [variant],
  );
  if (infiniteScrollState) {
    const noMoreMessage = formatMessage({
      id: 'visitorRegistration.labels.noMoreVisits',
      defaultMessage: 'No more visits',
    });
    const retryButtonText = formatMessage({
      id: 'visitorRegistration.buttons.retry',
      defaultMessage: 'Retry',
    });
    return (
      <InfiniteScrollContainerView
        variant={variant}
        hasMore={infiniteScrollState.hasMore}
        isLoadingMore={infiniteScrollState.isLoadingMore}
        observerTarget={infiniteScrollState.observerTarget}
        error={infiniteScrollState.error}
        retry={infiniteScrollState.retry}
        nextOffset={infiniteScrollState.nextOffset}
        noMoreMessage={noMoreMessage}
        retryButtonText={retryButtonText}
        wrapInTableRow={wrapInTableRow}
      >
        {children}
      </InfiniteScrollContainerView>
    );
  }
  return (
    <InfiniteScrollContainerWithHook buildingId={buildingId} after={after} before={before} variant={variant}>
      {children}
    </InfiniteScrollContainerWithHook>
  );
};
